/**
 * Checks whether a given HTML element is visible in the viewport by at least a specified threshold.
 *
 * This function calculates the visible area of the element within the current viewport
 * and compares it to the element's total area to determine the visibility ratio.
 *
 * @param el - The HTML element to check.
 * @param threshold - A value between 0 and 1 representing the minimum percentage of the element
 *                    that must be visible. For example, 0.1 means 10% of the element's area
 *                    must be within the viewport. Defaults to 0.1.
 *
 * @returns true if the visible area is equal to or greater than the threshold, false otherwise.
 *
 * Why use a threshold?
 * Unlike strict full-viewport checks, the threshold allows for partial visibility detection.
 * This is useful for triggering animations, lazy loading, or scroll-based effects when an element
 * becomes *partially* visible, rather than waiting for full visibility.
 */
export default function isElementInViewport(el: HTMLElement, threshold: number = 0.1): boolean {
  const rect = el.getBoundingClientRect();

  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

  const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
  const visibleWidth = Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0);

  const elementArea = rect.width * rect.height;
  const visibleArea = Math.max(0, visibleWidth) * Math.max(0, visibleHeight);

  const visibilityRatio = visibleArea / elementArea;

  return visibilityRatio >= threshold;
}
